import React from 'react';
import styled from 'styled-components';
import { useCustomerViewModel } from '../viewmodels/CustomerViewModel';
import CustomerSearchBar from '../components/CustomerSearchBar';
import CustomerListTable from '../components/CustomerListTable';
import Pagination from '../components/Pagination';
import PageHeader from '../components/ui/PageHeader';
import { MainContent } from '../styles/DashboardStyles';
import { colors, typography, spacing, borderRadius } from '../styles/designTokens';

// 페이지 컨테이너 스타일 (MainContent 기반)
const PageContainer = styled(MainContent)`
  background: ${colors.secondary[50]};
  min-height: calc(100vh - 48px);
`;

// 콘텐츠 영역 스타일
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

// 에러 상태 컨테이너 스타일
const ErrorContainer = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[6]};
  text-align: center;
`;

// 에러 아이콘 스타일
const ErrorIcon = styled.div`
  font-size: ${spacing[12]};
  margin-bottom: ${spacing[4]};
  opacity: 0.85;
`;

// 에러 제목 스타일
const ErrorTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.error[600]};
  margin: 0 0 ${spacing[2]} 0;
`;

// 에러 설명 스타일
const ErrorDescription = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.error[800]};
  margin: 0 0 ${spacing[4]} 0;
  line-height: ${typography.lineHeight.relaxed};
`;

// 재시도 버튼 스타일
const RetryButton = styled.button`
  background: ${colors.error[600]};
  color: ${colors.semantic.background.primary};
  border: none;
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: background-color 0.2s;

  &:focus-visible {
    outline: 2px solid ${colors.error[700]};
    outline-offset: 2px;
  }
  
  &:hover {
    background: ${colors.error[700]};
  }
`;

// 고객 관리 페이지 컴포넌트
const Customers: React.FC = () => {
  // ViewModel 훅 사용
  const {
    customers,
    loading,
    error,
    searchQuery,
    currentPage,
    limit,
    totalPages,
    total,
    handleSearch,
    handlePageChange,
    handleLimitChange,
    refreshData,
    formatDateTime,
    formatCurrency,
    formatMarketingStatus
  } = useCustomerViewModel();

  return (
    <PageContainer>
      {/* 페이지 헤더 */}
      <PageHeader
        title="고객 관리"
        description="등록된 고객 정보를 조회하고 관리할 수 있습니다."
      />

      {/* 콘텐츠 영역 */}
      <ContentArea>
        {/* 검색 바 */}
        <CustomerSearchBar
          searchQuery={searchQuery}
          limit={limit}
          total={total}
          loading={loading}
          onSearch={handleSearch}
          onLimitChange={handleLimitChange}
          onRefresh={refreshData}
        />

        {/* 에러 상태 */}
        {error && (
          <ErrorContainer>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle>데이터 로드 실패</ErrorTitle>
            <ErrorDescription>
              고객 목록을 불러오는 중 오류가 발생했습니다.<br />
              네트워크 연결을 확인하고 다시 시도해주세요.
            </ErrorDescription>
            <RetryButton onClick={refreshData}>
              다시 시도
            </RetryButton>
          </ErrorContainer>
        )}

        {/* 고객 목록 테이블 */}
        <CustomerListTable
          customers={customers}
          loading={loading}
          formatDateTime={formatDateTime}
          formatCurrency={formatCurrency}
          formatMarketingStatus={formatMarketingStatus}
        />

        {/* 페이지네이션 */}
        {!loading && !error && total > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={handlePageChange}
          />
        )}
      </ContentArea>
    </PageContainer>
  );
};

export default Customers; 